import { autoinject, transient, observable, BindingEngine, computedFrom, useView } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { BaseInspectorTab } from "../base-inspector-tab";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../../utilities/guid";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import * as moment from "moment";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../models/basics/basics.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { RightShipInspectionStatusEnum } from "../../../../../models/rightship-inspections/rightship-inspections.models";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject()
@transient()
@useView('views/pages/admin/inspectors/tabs/inspector-details.html')
export class InspectorDetails extends BaseInspectorTab {
    isMobileFilterOpen: boolean;
    @observable()
    filter: string;
    appliedFilter: string;
    public defaultPageCount: number = 50;
    public getItemRoute = (id) => `/insp/${id}?source=activities`;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    public portEntityTypes: EntityTypeConfigurationModel[];
    public countryEntityTypes: EntityTypeConfigurationModel[];

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, router: Router, private dialog:DialogClientService,
        private userClaimsService: UserClaimsService, private entityTypeConfigurationHelper:EntityTypeConfigurationHelper) {
        super(i18N, router, bindingEngine);
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.countryEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Countries"]);

        //this.columns.push();
        this.initialSorting = this.getSorting();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'upcomingInspectionDate',
                title: 'Upcoming Inspection Date'
            });
        return columns;
    }

    protected setDefaultFilters() {
        this.mobileFilterApply(false);
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        this.columns = this.getColumns();
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/Inspectors",
            columns: this.columns,
            initialFilters: filters,
            initialSorting: this.initialSorting
        };
    }

    private setItemSource() {

        const currentFilters: QueryFilter[] = [];
        
        if (this.inspectionCompanyFilterActive) {

            const inspectionCompanyFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "inspectingCompanyId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedInspectionCompanies || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(inspectionCompanyFilter);
        }

        if (this.accreditationStatusFilterActive) {

            const accreditationStatusFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "accreditationStatusId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedAccreditationStatuses || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(accreditationStatusFilter);
        }
        

        if (this.portFilterActive) {

            const portFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "portId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedPorts || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(portFilter);
        }

        if (this.countryFilterActive) {

            const countryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "countryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCountries || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(countryFilter);
        }

        if (this.cityFilterActive) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("cityOfResidence", this.selectedCity, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
        }

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("inspectorName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("inspectionCompany", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("cityOfResidence", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("country", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        this.itemSource = this.getItemSource(currentFilters);
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    @computedFrom(Reflection.getPropertyPath<InspectorDetails>(z => z.showColumnSorting),
    Reflection.getPropertyPath<InspectorDetails>(z => z.showInspectionCompanyFilter),
    Reflection.getPropertyPath<InspectorDetails>(z => z.showAccreditationStatusFilter),
    Reflection.getPropertyPath<InspectorDetails>(z => z.showPortFilter),
    Reflection.getPropertyPath<InspectorDetails>(z => z.showCountryFilter),
    Reflection.getPropertyPath<InspectorDetails>(z => z.showCityFilter))
    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting || this.showInspectionCompanyFilter || this.showAccreditationStatusFilter || this.showPortFilter
        || this.showCountryFilter || this.showCityFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }
        this.showInspectionCompanyFilter = false;
        this.showAccreditationStatusFilter = false;
        this.showPortFilter = false;
        this.showCountryFilter = false;
        this.showCityFilter = false;
        this.showColumnSorting = false;
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    clearAllFilters($event, reload: boolean = true) {
        if ($event)
            $event.stopPropagation();

        this.appliedFilter = null;
        this.filter = null;
        this.selectedInspectionCompanies = [];
        this.selectedAccreditationStatuses = [];
        this.selectedPorts = [];
        this.clearPortOnLoad = true;
        this.selectedCountries = [];

        this.closeAllFilters();

        if (reload)
            this.setItemSource();
    }

    public get mobileFilterSelected() {
        return this.showColumnSorting  || this.showPortFilter || this.showInspectionCompanyFilter || this.showAccreditationStatusFilter
        || this.showCountryFilter  || this.showCityFilter;
    }

    public closeAllFilters() {
        this.showColumnSorting = false;
        this.showInspectionCompanyFilter = false;
        this.showAccreditationStatusFilter = false;
        this.showPortFilter = false;
        this.showCountryFilter = false;
        this.showCityFilter = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }


    //#region column sorting 

    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion

    inspectorClick(item){
        this.dialog.show(RightShipDialogServiceConstants.URLs.InspectorDialog, item, true, false, false, "right");
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                name: 'inspectionCompany',
                title: 'Inspection Co.',
                isSortable: true,
            },
            {
                columnOrder: 2,
                sortOrder: 2,
                dataType: DataType.CustomTemplate,
                name: 'inspectorName',
                title: 'Inspector Name',
                value: '<a class="link" mousedown.trigger="itemClick(item)">${item.inspectorName}</a>',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null,
                    itemClick: this.inspectorClick.bind(this)
                }
            },
            {
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'country',
                title: 'Country',
                isSortable: true,
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                name: 'cityOfResidence',
                title: 'City of Residence',
                isSortable: true,
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Default,
                name: 'accreditation',
                title: 'Accreditation',
                isSortable: true,
            }, {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.DateTime,
                name: 'accreditationExpiryDate',
                title: 'Accreditation expiry',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY')
                    : '',
            },  {
                    dataType: DataType.CustomTemplate,
                    value: '<div class="column" if.bind="showInspectorDetails(item)">' +
                        '<div class="row align-items-middle">' +
                        '<a class="link" href.bind="getItemRoute(item.nextRightShipInspectionId)" mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                        '</div>' +
                        '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span></div>' +
                        '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span></div>' +
                        '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                    name: "vesselName",
                    title: "Upcoming Inspection",
                    instancePerRow: true,
                    sortOrder: 7,
                    columnOrder: 7,
                    isSortable: true,
                    parameter: {
                        item: null,
                        showInspectorDetails:(item:any)=>{
                            if(item.nextRightShipInspectionId && item.rightShipInspectionStatusId) {
                                const date= moment(item.upcomingInspectionDate).format('DD MMM YYYY');                            
                                const currentDate= moment(new Date()).format('DD MMM YYYY');
                                return (item.rightShipInspectionStatusId == RightShipInspectionStatusEnum.Cancelled && date >= currentDate) ? false : true;
                              }
                            return true;
                        },
                        getItemRoute: this.getItemRoute.bind(this),
                        itemClick: this.itemClick.bind(this)
                    }
                }, {
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.CustomTemplate,
                name: 'upcomingInspectionDate',
                title: 'Upcoming Inspection date',
                isSortable: true,
                instancePerRow: true,
                value:'<span if.bind="showInspectorDetails(item)">${formatDate(item.upcomingInspectionDate)}</span>',
                parameter: {
                    item: null,
                    showInspectorDetails:(item:any)=> this.showInspectionDetails(item),
                    formatDate: (date: Date | string) => {
                        return date && moment(date).format('DD MMM YYYY');
                    }
                },
                valueConverter: (date: Date | string) => date 
                    ? moment(date).format('DD MMM YYYY')
                    : '',
            }, 
            {
                columnOrder: 9,
                sortOrder: 9,
                dataType: DataType.CustomTemplate,
                name: 'totalInspections',
                title: 'Total Inspections (Calendar Year)',
                isSortable: true,
                instancePerRow: true,
                value: '<span if.bind="showInspectorDetails(item)">${item.totalInspections} (${item.totalInspectionsThisYear})</span> <span else>0 (0)</span>',
                parameter: {
                    item: null,
                    showInspectorDetails:(item:any)=> this.showInspectionDetails(item)
                }
            }, {
                columnOrder: 10,
                sortOrder: 10,
                dataType: DataType.CustomTemplate,
                name: 'port',
                title: 'Port',
                isSortable: true,
                instancePerRow: true,
                value: '<span if.bind="showInspectorDetails(item)">${item.port}</span>',
                parameter: {
                    item: null,
                    showInspectorDetails:(item:any)=> this.showInspectionDetails(item)
                }
            }, {
                columnOrder: 11,
                sortOrder: 11,
                dataType: DataType.Boolean,
                name: 'isActive',
                title: 'Is Active',
                isSortable: true
            }];
    }

    public showInspectionCompanyFilter: boolean = false;
    public showAccreditationStatusFilter: boolean = false;
    public showPortFilter: boolean = false;
    public showCountryFilter: boolean = false;
    public showCityFilter: boolean = false;
    public selectedInspectionCompanies: number[] = [];
    public selectedAccreditationStatuses: number[] = [];
    public selectedPorts: number[] = [];
    public selectedCountries: number[] = [];
    public selectedCity: string = "";
    public clearPortOnLoad: boolean = false;

    public showInspectionDetails(item: any) {
        if(item && item.nextRightShipInspectionId && item.rightShipInspectionStatusId){
            const date= moment(item.upcomingInspectionDate).format('DD MMM YYYY');                            
            const currentDate= moment(new Date()).format('DD MMM YYYY');
            return (item.rightShipInspectionStatusId == RightShipInspectionStatusEnum.Cancelled && date >= currentDate) ? false : true;
          }
          return true;
    }

    public toggleInspectionCompanyFilters() {
        if (!this.showInspectionCompanyFilter) {
            this.closeAllFilters();
        }
        this.showInspectionCompanyFilter = !this.showInspectionCompanyFilter;
        this.selectedFilter = {
            title: "Inspection Company",
            clear: () => {
                this.clearInspectionCompanyFilter();
            },
            apply: () => {
                this.applyInspectionCompanyFilter(this.selectedInspectionCompanies.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public toggleAccreditationStatusFilters() {
        if (!this.showAccreditationStatusFilter) {
            this.closeAllFilters();
        }
        this.showAccreditationStatusFilter = !this.showAccreditationStatusFilter;
        this.selectedFilter = {
            title: "Accreditation Status",
            clear: () => {
                this.clearAccreditationStatusFilter();
            },
            apply: () => {
                this.applyAccreditationStatusFilter(this.selectedAccreditationStatuses.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    public togglePortFilters() {
        if (!this.showPortFilter) {
            this.closeAllFilters();
        }
        this.showPortFilter = !this.showPortFilter;
        this.selectedFilter = {
            title: "Port",
            clear: () => {
                this.clearPortFilter();
            },
            apply: () => {
                this.applyPortFilter(this.selectedPorts.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public toggleCountryFilters() {
        if (!this.showCountryFilter) {
            this.closeAllFilters();
        }
        this.showCountryFilter = !this.showCountryFilter;
        this.selectedFilter = {
            title: "Country",
            clear: () => {
                this.clearCountryFilter();
            },
            apply: () => {
                this.applyCountryFilter(this.selectedCountries.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public toggleCityFilters() {
        if (!this.showCityFilter) {
            this.closeAllFilters();
        }
        this.showCityFilter = !this.showCityFilter;
        this.selectedFilter = {
            title: "City",
            clear: () => {
                this.clearCityFilter();
            },
            apply: () => {
                this.applyCityFilter(this.selectedCity, false);
            }
        }
    }
    
    public applyPortFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedPorts = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.togglePortFilters();
    }
    
    public applyCountryFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedCountries = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleCountryFilters();
    }
    
    public applyCityFilter(city, reload: boolean = true) {
        this.selectedCity = city;
        if (reload == true) this.setItemSource();
        this.toggleCityFilters();
    }

    public applyInspectionCompanyFilter(items: any[], reload: boolean = true) {
        this.selectedInspectionCompanies = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleInspectionCompanyFilters();
    }

    public applyAccreditationStatusFilter(items: any[], reload: boolean = true) {
        this.selectedAccreditationStatuses = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleAccreditationStatusFilters();
    }

    public clearInspectionCompanyFilter() {
        this.selectedInspectionCompanies = [];
        this.setItemSource();
    }
    public clearAccreditationStatusFilter() {
        this.selectedAccreditationStatuses = [];
        this.setItemSource();
    }
    
    public clearPortFilter() {
        this.selectedPorts = [];
        this.clearPortOnLoad = true;
        this.setItemSource();
    }

    public clearCountryFilter() {
        this.selectedCountries = [];
        this.setItemSource();
    }

    public clearCityFilter() {
        this.selectedCity = "";
        this.setItemSource();
    }

    @computedFrom("selectedInspectionCompanies", "selectedInspectionCompanies.length")
    public get inspectionCompanyFilterActive() {
        return this.selectedInspectionCompanies && this.selectedInspectionCompanies.length > 0;
    }

    @computedFrom("selectedAccreditationStatuses", "selectedAccreditationStatuses.length")
    public get accreditationStatusFilterActive() {
        return this.selectedAccreditationStatuses && this.selectedAccreditationStatuses.length > 0;
    }
    
    @computedFrom("selectedPorts", "selectedPorts.length")
    public get portFilterActive() {
        return this.selectedPorts && this.selectedPorts.length > 0;
    }
    
    @computedFrom("selectedCountries", "selectedCountries.length")
    public get countryFilterActive() {
        return this.selectedCountries && this.selectedCountries.length > 0;
    }
    
    @computedFrom("selectedCity")
    public get cityFilterActive() {
        return this.selectedCity;
    }
}